import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, ChevronRight, Clock, UserMinus, AlertTriangle, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface LeaveOverviewData {
  pendingCount: number;
  todayAbsences: number;
  upcomingHeavyDays: Array<{ date: string; count: number }>;
  lowBalanceAlerts: number;
}

export default function LeaveOverviewWidget() {
  const [, navigate] = useLocation();

  const { data } = useQuery<LeaveOverviewData>({
    queryKey: ["/api/dashboard/leave-overview"],
  });

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-md">
              <CalendarDays className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-lg font-bold">Leave Overview</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-950/30 gap-1 font-semibold h-8 px-2"
            onClick={() => navigate("/leave-approvals")}
          >
            Manage <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {!data ? (
          <div className="text-center py-6 text-muted-foreground">
            <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Loading leave data...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Pending', value: data.pendingCount, icon: Clock, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30' },
                { label: 'Absent Today', value: data.todayAbsences, icon: UserMinus, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/30' },
                { label: 'Low Balance', value: data.lowBalanceAlerts, icon: AlertTriangle, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/30' },
                {
                  label: 'Heavy Days',
                  value: data.upcomingHeavyDays.length,
                  icon: CalendarClock,
                  color: 'text-blue-600 dark:text-blue-400',
                  bg: 'bg-blue-50 dark:bg-blue-950/30',
                },
              ].map(({ label, value, icon: Icon, color, bg }) => (
                <div key={label} className={cn("rounded-xl p-3 text-center", bg)}>
                  <div className={cn("flex items-center justify-center gap-1.5 mb-1", color)}>
                    <Icon className="h-4 w-4" />
                    <span className="text-2xl font-bold">{value}</span>
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">{label}</span>
                </div>
              ))}
            </div>

            {/* Upcoming heavy days */}
            {data.upcomingHeavyDays.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Upcoming High-Absence Days
                </h4>
                <div className="space-y-1.5">
                  {data.upcomingHeavyDays.slice(0, 4).map((day) => (
                    <div key={day.date} className="flex items-center justify-between text-sm p-2 rounded-lg bg-muted/50">
                      <span className="text-foreground font-medium">
                        {format(new Date(day.date), 'EEE, MMM d')}
                      </span>
                      <span className="text-rose-600 dark:text-rose-400 font-bold">{day.count} absent</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
